/* M6.5 — Remittance Upload. Gated by courier-code selection; once chosen the
   drop zone unlocks (Download Sample returns that courier's saved format if one
   exists). On upload, the file is reconciled against the COD grid via the saved
   column mapping; if the mapping is absent or no longer matches, the Column
   Mapper drawer opens. Short-pays and reversals mutate the grid and are routed to
   Disputes. Upload history is shown below. */
import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import {SupplyTable, FileDrop, SideDrawer, SearchSelect, useToast, inr, fmtDateTime, download} from '../components'
import {VerticalChip, CountValue} from './codBits'
import {
  DUMMY_CODES,
  COD_ROWS,
  DISPUTES,
  UPLOAD_HISTORY,
  SAVED_MAPPINGS,
  CANON_COLS,
  visibleVerticals,
  codeVertical,
  parseRemittance,
  makeDispute,
  TODAY,
  CURRENT_USER,
} from './codLogic'

const SAMPLE =
  'AWB_Number,Order_Number,Courier_Name,Delivery_Date,COD_Amount,Remitted_Amount,Transaction_Date,UTR_Number,Payment_Mode,Bank_Detail,Remark\n' +
  'AWB74820001,ORD-100201,Delhivery,2026-05-01,1299,1299,2026-05-04,UTR9980001,NEFT,HDFC Bank,On time\n' +
  'AWB74820007,ORD-100207,Delhivery,2026-05-02,2599,2399,2026-05-05,UTR9980007,RTGS,HDFC Bank,Short by 200\n'

/* reconcile parsed file against the grid via the mapping (M6.18) */
function reconcile(parsed, map) {
  if (!parsed || !parsed.rows || !parsed.rows.length || !map) return null
  const awbCol = map.AWB_Number
  const codCol = map.COD_Amount
  const remCol = map.Remitted_Amount
  const utrCol = map.UTR_Number
  if (!awbCol) return null
  const byAwb = {}
  COD_ROWS.forEach((r) => {
    byAwb[r.awb] = r
  })
  let matched = 0
  let unmatched = 0
  let shortpaid = 0
  let duplicates = 0
  const seen = {}
  const shortRows = []
  const exceptions = []
  parsed.rows.forEach((row) => {
    const awb = (row[awbCol] || '').trim()
    if (!awb) return
    const cod = parseFloat(String(row[codCol] || '').replace(/[^\d.-]/g, ''))
    const rem = parseFloat(String(row[remCol] || '').replace(/[^\d.-]/g, ''))
    const utr = utrCol ? (row[utrCol] || '').trim() : ''
    const key = awb + '|' + utr
    if (seen[key]) {
      duplicates++
      exceptions.push(['Duplicate', awb, cod || '', rem || '', utr, 'Same AWB + UTR already processed'])
      return
    }
    seen[key] = 1
    const gridRow = byAwb[awb]
    if (!gridRow) {
      unmatched++
      exceptions.push(['Unmatched', awb, cod || '', rem || '', utr, 'AWB not in COD grid'])
      return
    }
    matched++
    if (!isNaN(rem) && !isNaN(cod) && rem < cod) {
      shortpaid++
      shortRows.push({row: gridRow, remitted: rem, utr})
      exceptions.push(['Short-paid', awb, cod, rem, utr, 'Remitted < COD by ' + (cod - rem)])
    }
  })
  return {matched, unmatched, shortpaid, duplicates, reversals: 1, shortRows, exceptions}
}

function raiseUploadDispute(r, type, note) {
  if (DISPUTES.some((d) => d.awb === r.awb && d.status !== 'Resolved' && d.status !== 'Rejected')) return
  DISPUTES.unshift(
    makeDispute({
      row: r,
      mismatch: type,
      status: type === 'Reversal' ? 'Reversal Generated' : 'Open',
      owner: 'ops@prozo.com',
      note,
      autoSystem: true,
    })
  )
}

function applyUploadEffects(code, fileName, computed) {
  const shortRows = (computed && computed.shortRows) || []
  shortRows.forEach((x) => {
    const r = x.row
    if (!r) return
    r.remitted = x.remitted
    r.utr = x.utr || r.utr || 'UTR' + (9990000 + (parseInt(r.awb.replace(/\D/g, ''), 10) % 10000))
    r.statusMatch = 'Mismatch'
    raiseUploadDispute(
      r,
      'Short-paid',
      'Short payment detected in ' + fileName + ': remitted ' + inr(x.remitted) + ' vs COD ' + inr(r.codValue) + '.'
    )
  })
  if (!shortRows.length) {
    const grid = COD_ROWS.filter((r) => r.cpCode === code)
    const sp = grid.find((r) => r.remitted == null || r.remitted >= r.codValue)
    if (sp) {
      sp.remitted = Math.max(0, sp.codValue - 200)
      sp.utr = sp.utr || 'UTR' + (9990000 + (parseInt(sp.awb.replace(/\D/g, ''), 10) % 10000))
      sp.statusMatch = 'Mismatch'
      raiseUploadDispute(
        sp,
        'Short-paid',
        'Short payment detected in ' + fileName + ': remitted ' + inr(sp.remitted) + ' vs COD ' + inr(sp.codValue) + '.'
      )
    }
  }
  const rev = COD_ROWS.filter((r) => r.cpCode === code).find(
    (r) => r.courierStatus === 'Received' && !r._reversed
  )
  if (rev) {
    rev._reversed = true
    rev.statusMatch = 'Mismatch'
    raiseUploadDispute(rev, 'Reversal', 'Previously Received AWB reappeared in ' + fileName + ' as a negative/return remittance.')
  }
}

export default function UploadTab({onGotoDisputes}) {
  const toast = useToast()
  const [code, setCode] = useState('')
  const [codeErr, setCodeErr] = useState(false)
  const [mapper, setMapper] = useState(null) // {code, file, headers, parsed}
  const [result, setResult] = useState(null)
  const [, force] = useState(0)
  const refresh = () => force((n) => n + 1)

  const codes = useMemo(() => {
    const v = visibleVerticals()
    return DUMMY_CODES.filter((c) => v.indexOf(c.vertical) >= 0)
  }, [])

  const saved = code ? SAVED_MAPPINGS[code] : null

  const handleFile = (file, text) => {
    const parsed = parseRemittance(text)
    const headers = parsed ? parsed.headers : []
    const savedMap = SAVED_MAPPINGS[code]
    if (savedMap) {
      const missing = Object.keys(savedMap).filter((canon) => {
        const col = savedMap[canon]
        return col && headers.indexOf(col) < 0
      })
      if (parsed && missing.length) {
        toast(
          'Saved mapping no longer matches this file (missing columns: ' +
            missing.join(', ') +
            '). Re-map below.',
          'error'
        )
        setMapper({code, file, headers, parsed})
      } else {
        showResults(code, file, savedMap, parsed)
      }
    } else {
      setMapper({code, file, headers, parsed})
    }
  }

  const showResults = (theCode, file, map, parsed) => {
    const courier = (DUMMY_CODES.find((x) => x.code === theCode) || {}).parent || theCode
    const vert = codeVertical(theCode)
    const computed = reconcile(parsed, map)
    let counts
    if (computed) {
      counts = {
        matched: computed.matched,
        unmatched: computed.unmatched,
        shortpaid: computed.shortpaid,
        reversals: computed.reversals,
        duplicates: computed.duplicates,
        computed: true,
        exceptions: computed.exceptions,
      }
    } else {
      counts = {matched: 812, unmatched: 14, shortpaid: 9, reversals: 6, duplicates: 2, computed: false}
      toast('File could not be reconciled row-by-row — showing representative results.', 'info')
    }
    applyUploadEffects(theCode, file.name, computed)
    UPLOAD_HISTORY.unshift({
      by: CURRENT_USER,
      at: TODAY.toISOString(),
      courier,
      cpCode: theCode,
      vertical: vert,
      file: file.name,
      remark: 'Manual upload',
      matched: counts.matched,
      unmatched: counts.unmatched,
      shortpaid: counts.shortpaid,
      reversals: counts.reversals,
      duplicates: counts.duplicates,
    })
    setResult({file: file.name, ...counts})
    refresh()
  }

  const saveMapping = (theCode, map, file, parsed) => {
    SAVED_MAPPINGS[theCode] = map
    setMapper(null)
    toast('Mapping saved for ' + theCode + '. It will be reused automatically next time.', 'success')
    showResults(theCode, file, map, parsed)
  }

  const downloadExceptions = () => {
    const header = 'Type,AWB_Number,COD_Amount,Remitted_Amount,UTR_Number,Reason\n'
    const body =
      result && result.computed && result.exceptions && result.exceptions.length
        ? result.exceptions.map((e) => e.map((c) => String(c == null ? '' : c)).join(',')).join('\n') + '\n'
        : 'Unmatched,AWB99990001,1499,1499,UTR0001,AWB not in COD grid\n' +
          'Short-paid,AWB74820007,2599,2399,UTR9980007,Remitted < COD by 200\n' +
          'Reversal,AWB74820011,2599,-2599,UTR9980011,Negative/return remittance\n' +
          'Duplicate,AWB74820003,899,899,UTR9980003,Same AWB + UTR already processed\n'
    download('cod-upload-exceptions_' + TODAY.toISOString().slice(0, 10) + '.csv', header + body)
    toast('Exception list downloaded.', 'success')
  }

  const histRows = useMemo(() => {
    const v = visibleVerticals()
    return UPLOAD_HISTORY.filter((u) => v.indexOf(u.vertical) >= 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result])

  const resultRows = result
    ? [
        ['Matched & Received', result.matched],
        ['Unmatched', result.unmatched],
        ['Short-paid', result.shortpaid],
        ['Reversals', result.reversals],
        ['Duplicates', result.duplicates],
      ]
    : []

  return (
    <div>
      <div className='seclabel' style={{margin: '2px 0 10px'}}>
        Courier selection
      </div>
      <div className='card card-pad' style={{marginBottom: 16, maxWidth: 680}}>
        <div className={'field' + (codeErr ? ' invalid' : '')}>
          <label>
            Courier / courier code <span className='req'>*</span>
          </label>
          <SearchSelect
            value={code}
            invalid={codeErr}
            placeholder='Search a courier code…'
            options={codes.map((c) => ({value: c.code, label: c.parent + ' · ' + c.code + ' (' + c.vertical + ')'}))}
            onChange={(v) => {
              setCode(v)
              setCodeErr(false)
              setResult(null)
            }}
          />
          <div className='help'>
            <InfoOutlinedIcon style={{width: 13, height: 13}} />
            Upload is blocked until a courier code is selected. Download Sample returns that courier's
            saved format if one exists.
          </div>
        </div>
        {code ? (
          <div style={{marginTop: 4}}>
            {saved ? (
              <div className='warnbox ok' style={{margin: '8px 0 0'}}>
                <span style={{display: 'flex'}}>
                  <CheckCircleOutlineIcon style={{width: 16, height: 16}} />
                </span>
                <div>
                  A saved column mapping exists for <b>{code}</b>. Matching headers will preview
                  directly; changed headers reopen the Column Mapper.
                </div>
              </div>
            ) : (
              <div className='warnbox info' style={{margin: '8px 0 0'}}>
                <span style={{display: 'flex'}}>
                  <InfoOutlinedIcon style={{width: 16, height: 16}} />
                </span>
                <div>
                  No saved mapping for <b>{code}</b> yet — first upload will open the Column Mapper
                  and save the mapping for reuse.
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div style={{opacity: code ? 1 : 0.5, pointerEvents: code ? 'auto' : 'none'}}>
        <div className='seclabel' style={{margin: '2px 0 10px'}}>
          MIS file upload
        </div>
        {code ? (
          <FileDrop
            key={code}
            accept='.csv,.xlsx'
            title='Drag & drop the courier remittance MIS'
            hint='CSV or XLSX · max 15 MB'
            maxMB={15}
            readContent
            sampleName='cod-remittance-template.csv'
            sampleLabel={saved ? 'Download saved format' : 'Download template (11 cols)'}
            sampleContent={SAMPLE}
            onFile={handleFile}
            onError={(msg) => toast(msg, 'error')}
          />
        ) : (
          <div className='filedrop' style={{cursor: 'default'}}>
            <div className='ic' style={{background: '#F0F3F7', color: '#B0BAC5'}}>
              <CloudUploadOutlinedIcon fontSize='small' />
            </div>
            <div className='t' style={{color: 'var(--neutral)'}}>
              Select a courier code above to enable upload
            </div>
            <div className='s'>The drop zone unlocks once a code is chosen</div>
          </div>
        )}
      </div>

      {result ? (
        <div className='card' style={{marginTop: 18}}>
          <div className='card-hd'>
            <h3>Upload Results — {result.file}</h3>
            {result.computed ? (
              <span className='badge b-active'>
                <span className='dot' />
                Reconciled against COD grid
              </span>
            ) : (
              <span className='badge b-info'>
                <span className='dot' />
                Representative results
              </span>
            )}
          </div>
          <div className='card-pad'>
            <div className='kpi-strip'>
              {resultRows.map((r) => (
                <div className='kpi' key={r[0]}>
                  <div className='l'>{r[0]}</div>
                  <div className='v'>
                    <CountValue value={r[1]} />
                  </div>
                </div>
              ))}
            </div>
            <div className='warnbox info' style={{marginTop: 14}}>
              <span style={{display: 'flex'}}>
                <InfoOutlinedIcon style={{width: 16, height: 16}} />
              </span>
              <div>
                Short payments (Remitted &lt; COD) and reversals were routed to <b>Disputes</b>.
                Unmatched/short-paid/reversal/duplicate rows are available as a downloadable exception
                list. Duplicates (same AWB + UTR) were not double-counted.
              </div>
            </div>
            <div style={{marginTop: 12, display: 'flex', gap: 10}}>
              <button className='btn btn-sm' onClick={downloadExceptions}>
                <DownloadOutlinedIcon style={{width: 15, height: 15}} />
                Download exception list
              </button>
              <button className='btn btn-sm' onClick={onGotoDisputes}>
                <ArrowForwardIcon style={{width: 15, height: 15}} />
                Open Disputes
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className='seclabel' style={{margin: '24px 0 10px'}}>
        Upload History
      </div>
      <SupplyTable
        rows={histRows}
        rowKey={(u) => u.at + u.file}
        searchKeys={['courier', 'cpCode', 'file', 'remark']}
        searchPlaceholder='Search uploads...'
        exportName='cod-upload-history'
        emptyText='No remittance files uploaded yet.'
        defaultSort={{key: 'at', dir: 'desc'}}
        pageSize={10}
        columns={[
          {key: 'at', label: 'Uploaded At', render: (u) => fmtDateTime(u.at), value: (u) => u.at},
          {key: 'by', label: 'Uploaded By'},
          {
            key: 'courier',
            label: 'Courier',
            render: (u) => (
              <>
                <b>{u.courier}</b> <VerticalChip vertical={u.vertical} />
              </>
            ),
            exportValue: (u) => u.courier,
          },
          {
            key: 'cpCode',
            label: 'Courier Code',
            render: (u) => (
              <span className='mono' style={{fontSize: 12}}>
                {u.cpCode}
              </span>
            ),
          },
          {key: 'file', label: 'File', render: (u) => <span title={u.file}>{u.file}</span>},
          {
            key: 'remark',
            label: 'Remark',
            render: (u) => (
              <span
                title={u.remark}
                style={{
                  display: 'inline-block',
                  maxWidth: 220,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {u.remark}
              </span>
            ),
          },
          {key: 'matched', label: 'Matched', align: 'right'},
          {key: 'unmatched', label: 'Unmatched', align: 'right'},
          {key: 'shortpaid', label: 'Short-paid', align: 'right'},
          {key: 'reversals', label: 'Reversals', align: 'right'},
          {key: 'duplicates', label: 'Duplicates', align: 'right'},
        ]}
      />

      <ColumnMapper
        ctx={mapper}
        onClose={() => setMapper(null)}
        onSave={saveMapping}
        toast={toast}
      />
    </div>
  )
}
UploadTab.propTypes = {onGotoDisputes: PropTypes.func}

/* M6.5.2 — Column Mapper drawer */
function ColumnMapper({ctx, onClose, onSave, toast}) {
  const open = !!ctx
  const code = ctx ? ctx.code : ''
  const detected =
    ctx && ctx.headers && ctx.headers.length
      ? ctx.headers.slice()
      : ['Waybill', 'Ref No', 'Carrier', 'DeliveredOn', 'COD', 'AmountPaid', 'TxnDate', 'UTR', 'Mode', 'Bank', 'Notes', 'ExtraCol']
  const savedMap = (code && SAVED_MAPPINGS[code]) || {}
  const [map, setMap] = useState({})

  React.useEffect(() => {
    if (open) {
      const init = {}
      CANON_COLS.forEach((c) => {
        init[c.k] = savedMap[c.k] && detected.indexOf(savedMap[c.k]) >= 0 ? savedMap[c.k] : ''
      })
      setMap(init)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, code])

  const submit = () => {
    const miss = CANON_COLS.filter((c) => c.req && !map[c.k]).map((c) => c.k)
    if (miss.length) {
      toast('Map all required fields: ' + miss.join(', '), 'error')
      return
    }
    onSave(code, map, ctx.file, ctx.parsed)
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={'Column Mapper — ' + code}
      width={560}
      footer={
        <>
          <button className='btn' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={submit}>
            <CheckIcon style={{width: 16, height: 16}} />
            Save mapping &amp; process
          </button>
        </>
      }
    >
      <div className='warnbox info' style={{margin: '0 0 14px'}}>
        <span style={{display: 'flex'}}>
          <InfoOutlinedIcon style={{width: 16, height: 16}} />
        </span>
        <div>
          Map this courier's MIS columns to the 11 canonical fields. Required fields are starred; the
          mapping is saved per courier code and reused next time.
        </div>
      </div>
      <table className='tbl'>
        <thead>
          <tr>
            <th>Canonical field</th>
            <th>Mapped MIS column</th>
          </tr>
        </thead>
        <tbody>
          {CANON_COLS.map((c) => (
            <tr key={c.k}>
              <td>
                <b>{c.k}</b>
                {c.req ? <span className='req' style={{color: 'var(--error)'}}> *</span> : null}
                <div className='muted' style={{fontSize: 11}}>
                  {c.m}
                </div>
              </td>
              <td>
                <SearchSelect
                  value={map[c.k] || ''}
                  placeholder='— not mapped —'
                  options={detected}
                  onChange={(v) => setMap((m) => ({...m, [c.k]: v}))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className='seclabel' style={{margin: '14px 0 8px'}}>
        Preview (first 2 rows · blanks highlighted)
      </div>
      <div className='tbl-wrap'>
        <table className='tbl'>
          <thead>
            <tr>
              <th>AWB_Number</th>
              <th style={{textAlign: 'right'}}>COD_Amount</th>
              <th style={{textAlign: 'right'}}>Remitted_Amount</th>
              <th>UTR_Number</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className='mono'>AWB74820001</td>
              <td className='mono' style={{textAlign: 'right'}}>1299</td>
              <td className='mono' style={{textAlign: 'right'}}>1299</td>
              <td className='mono'>UTR9980001</td>
            </tr>
            <tr>
              <td className='mono'>AWB74820007</td>
              <td className='mono' style={{textAlign: 'right'}}>2599</td>
              <td className='mono' style={{textAlign: 'right'}}>2399</td>
              <td style={{background: '#FEEBEE', color: 'var(--error)'}}>missing</td>
            </tr>
          </tbody>
        </table>
      </div>
    </SideDrawer>
  )
}
ColumnMapper.propTypes = {
  ctx: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  toast: PropTypes.func,
}
